import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';

const emptySectionRow = () => ({ id: null, name: '', capacity: 40 });

/** @param {Record<string, unknown>|undefined|null} cls */
export const classUsesSections = (cls) => {
  if (!cls) return true;
  if (cls.uses_sections === undefined || cls.uses_sections === null) return true;
  return Number(cls.uses_sections) === 1 || cls.uses_sections === true;
};

const normSubjectId = (id) => {
  const n = Number(id);
  return Number.isNaN(n) || n <= 0 ? null : n;
};

const subjectIdsFromClass = (cls) => (cls?.subjects || [])
  .map((s) => normSubjectId(s.id ?? s.subject_id))
  .filter((id) => id != null);

const parseNumericName = (value) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

export default function ClassFormDialog({
  open,
  onOpenChange,
  editingClass = null,
  academicYearId = null,
  onSuccess,
}) {
  const { canManageFeature } = useAuth();
  const canManageClasses = canManageFeature('classes');
  const canManageSubjects = canManageFeature('subjects');
  const { toast } = useToast();

  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '', numeric_name: '', description: '', uses_sections: false,
  });
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [sectionRows, setSectionRows] = useState([emptySectionRow()]);
  const [classStudents, setClassStudents] = useState([]);
  const [studentSectionChoice, setStudentSectionChoice] = useState({});
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setFormData({ name: '', numeric_name: '', description: '', uses_sections: false });
    setSelectedSubjectIds([]);
    setSectionRows([emptySectionRow()]);
    setClassStudents([]);
    setStudentSectionChoice({});
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setSubjects(response.data.data || []);
    } catch {
      console.error('Failed to fetch subjects');
    }
  };

  const loadStudentsForClass = async (classId, usesSections) => {
    if (!classId || !usesSections) {
      setClassStudents([]);
      setStudentSectionChoice({});
      return;
    }
    setStudentsLoading(true);
    try {
      const params = {
        class_id: classId,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const res = await api.get('/students', { params });
      const list = res.data.data || [];
      setClassStudents(list);
      const m = {};
      list.forEach((s) => {
        m[s.id] = s.section_id ? `id:${s.section_id}` : '__none__';
      });
      setStudentSectionChoice(m);
    } catch {
      setClassStudents([]);
      setStudentSectionChoice({});
      toast({ title: 'Error', description: 'Failed to load students for class', variant: 'destructive' });
    } finally {
      setStudentsLoading(false);
    }
  };

  const populateFromClass = (detail) => {
    const uses = classUsesSections(detail);
    setFormData({
      name: detail.name || '',
      numeric_name: detail.numeric_name?.toString() || '',
      description: detail.description || '',
      uses_sections: uses,
    });
    const secs = detail.sections || [];
    setSectionRows(
      uses && secs.length
        ? secs.map((s) => ({ id: s.id, name: s.name || '', capacity: s.capacity ?? 40 }))
        : [emptySectionRow()],
    );
    setSelectedSubjectIds(subjectIdsFromClass(detail));
    loadStudentsForClass(detail.id, uses);
  };

  useEffect(() => {
    if (!open) return;
    fetchSubjects();
    if (editingClass) {
      populateFromClass(editingClass);
    } else {
      resetForm();
    }
  }, [open, editingClass?.id]);

  const sectionSelectOptions = useMemo(() => {
    const opts = [{ value: '__none__', label: '— No section —' }];
    sectionRows.forEach((r) => {
      const nm = (r.name || '').trim();
      if (!nm) return;
      if (r.id) {
        opts.push({ value: `id:${r.id}`, label: `Section ${nm}` });
      } else {
        opts.push({ value: `name:${nm}`, label: `Section ${nm} (new)` });
      }
    });
    return opts;
  }, [sectionRows]);

  const buildSectionsPayload = () =>
    sectionRows
      .filter((r) => (r.name || '').trim())
      .map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        name: r.name.trim(),
        capacity: parseInt(String(r.capacity), 10) || 40,
      }));

  const buildStudentSectionsPayload = () =>
    classStudents.map((s) => {
      const v = studentSectionChoice[s.id] ?? '__none__';
      if (!v || v === '__none__') return { student_id: s.id, section_id: null };
      if (String(v).startsWith('id:')) {
        return { student_id: s.id, section_id: parseInt(v.slice(3), 10) };
      }
      if (String(v).startsWith('name:')) {
        return { student_id: s.id, section_name: v.slice(5) };
      }
      return { student_id: s.id, section_id: null };
    });

  const buildSubjectIdsPayload = () => selectedSubjectIds
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);

  const addSectionRow = () => {
    setSectionRows((rows) => [...rows, emptySectionRow()]);
  };

  const removeSectionRow = (index) => {
    setSectionRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== index)));
  };

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const subjectIds = buildSubjectIdsPayload();
    const uses = !!formData.uses_sections;
    const sectionsPayload = uses ? buildSectionsPayload() : [];

    setSubmitting(true);
    try {
      const data = {
        name: formData.name,
        numeric_name: parseNumericName(formData.numeric_name),
        description: formData.description,
        uses_sections: uses,
        academic_year_id: academicYearId || undefined,
        subject_ids: subjectIds,
        ...(uses ? { sections: sectionsPayload } : { sections: [] }),
      };

      let classId;
      if (editingClass) {
        if (uses) {
          data.student_sections = buildStudentSectionsPayload();
        }
        try {
          await api.put(`/classes/${editingClass.id}`, data);
        } catch (updateError) {
          const status = updateError?.response?.status;
          if (status === 500) {
            const { subject_ids: _omit, ...classPayload } = data;
            await api.put(`/classes/${editingClass.id}`, classPayload);
            await api.put(`/classes/${editingClass.id}/subjects`, { subject_ids: subjectIds });
          } else {
            throw updateError;
          }
        }
        classId = editingClass.id;
        toast({ title: 'Success', description: 'Class updated' });
      } else {
        const res = await api.post('/classes', data);
        classId = res.data?.data?.id;
        if (!classId) {
          toast({ title: 'Error', description: 'Class created but id missing in response', variant: 'destructive' });
          return;
        }
        toast({ title: 'Success', description: 'Class created' });
      }

      handleOpenChange(false);
      onSuccess?.(classId);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="border-border text-foreground max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingClass ? 'Edit Class' : 'Add New Class'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Class Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-muted border-border" placeholder="e.g., Class 10" required />
          </div>
          <div>
            <Label>Numeric Value</Label>
            <Input type="number" value={formData.numeric_name} onChange={(e) => setFormData({ ...formData, numeric_name: e.target.value })}
              className="bg-muted border-border" placeholder="e.g., 10" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-muted border-border" />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
            <div>
              <Label className="text-foreground">Uses sections</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Turn off for a single class group (no A/B sections). Class teachers are set on the class page.
              </p>
            </div>
            <Switch
              checked={!!formData.uses_sections}
              onCheckedChange={(checked) => {
                setFormData((prev) => ({ ...prev, uses_sections: checked }));
                if (!checked) {
                  setSectionRows([emptySectionRow()]);
                  setClassStudents([]);
                  setStudentSectionChoice({});
                }
              }}
            />
          </div>
          {(canManageSubjects || canManageClasses) && (
          <div>
            <Label>Assigned Subjects</Label>
            {subjects.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-2">No subjects found. Add subjects first.</p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto rounded-md border border-border p-2">
                {subjects.map((subject) => (
                  <label key={subject.id} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="accent-blue-500"
                      checked={selectedSubjectIds.some((sid) => normSubjectId(sid) === normSubjectId(subject.id))}
                      onChange={(e) => {
                        const subjectId = normSubjectId(subject.id);
                        if (!subjectId) return;
                        setSelectedSubjectIds((prev) => {
                          if (e.target.checked) {
                            if (prev.some((sid) => normSubjectId(sid) === subjectId)) return prev;
                            return [...prev, subjectId];
                          }
                          return prev.filter((sid) => normSubjectId(sid) !== subjectId);
                        });
                      }}
                    />
                    <span>{subject.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          )}

          {formData.uses_sections && (
          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-foreground">Sections</Label>
              <Button type="button" variant="outline" size="sm" className="border-border" onClick={addSectionRow}>
                <Plus className="w-4 h-4 mr-1" /> Add section
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Edit names/capacity, add rows, or remove a row to drop a section (only if no students remain on it).
            </p>
            <div className="space-y-2">
              {sectionRows.map((row, index) => (
                <div key={row.id ?? `new-${index}`} className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[100px]">
                    <Label className="text-xs text-muted-foreground">Name *</Label>
                    <Input
                      value={row.name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSectionRows((rows) => rows.map((r, i) => (i === index ? { ...r, name: v } : r)));
                      }}
                      className="bg-muted border-border"
                      placeholder="A"
                    />
                  </div>
                  <div className="w-24">
                    <Label className="text-xs text-muted-foreground">Capacity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={row.capacity}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSectionRows((rows) => rows.map((r, i) => (i === index ? { ...r, capacity: v } : r)));
                      }}
                      className="bg-muted border-border"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground shrink-0"
                    onClick={() => removeSectionRow(index)}
                    aria-label="Remove section"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          )}

          {editingClass && formData.uses_sections && (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <Label className="text-foreground">Students → section</Label>
              <p className="text-xs text-muted-foreground">Assign or change section per student. Save class to apply.</p>
              {studentsLoading ? (
                <p className="text-sm text-muted-foreground">Loading students…</p>
              ) : classStudents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No students in this class.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                  {classStudents.map((s) => (
                    <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                      <span className="text-foreground min-w-0 flex-1 truncate">
                        {s.first_name} {s.last_name}
                        {s.admission_no ? <span className="text-muted-foreground ml-1">({s.admission_no})</span> : null}
                      </span>
                      <Select
                        value={studentSectionChoice[s.id] ?? '__none__'}
                        onValueChange={(v) => setStudentSectionChoice((prev) => ({ ...prev, [s.id]: v }))}
                      >
                        <SelectTrigger className="w-full sm:w-48 bg-muted border-border text-foreground">
                          <SelectValue placeholder="Section" />
                        </SelectTrigger>
                        <SelectContent className="border-border">
                          {sectionSelectOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={submitting}>
            {submitting ? 'Saving…' : editingClass ? 'Update' : 'Create'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
